import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { getPasswordPolicyError, PASSWORD_RULES } from '../utils/passwordPolicy.ts';
import { isLocalMode } from '../utils/localMode.ts';
import ThemeToggle from './ThemeToggle.tsx';
import './Login.css';
import LegalLinks from './LegalLinks.tsx';

// Standard AWS Cognito "forgot password" flow, implemented with the Amplify
// auth SDK (the same SDK the Login screen uses):
//
//   Step 1 (request): resetPassword({ username }) emails a one-time code.
//   Step 2 (confirm): confirmResetPassword({ username, confirmationCode,
//                     newPassword }) sets the new password.
//
// The user pool has `prevent_user_existence_errors = ENABLED` (see
// terraform/cognito.tf), so Cognito does NOT reveal whether an email is
// registered — step 1 reports success either way. We mirror that here and never
// disclose account existence in the UI.
type Stage = 'request' | 'confirm' | 'done';

// Maps the most common Cognito exceptions to friendly copy, falling back to the
// SDK's own message. Keeps the happy-path code readable.
function friendlyError(err: any): string {
  switch (err?.name) {
    case 'CodeMismatchException':
      return 'That code is incorrect. Double-check it and try again.';
    case 'ExpiredCodeException':
      return 'That code has expired. Request a new one to continue.';
    case 'LimitExceededException':
      return 'Too many attempts. Please wait a little while and try again.';
    case 'InvalidPasswordException':
      return err.message || 'That password does not meet the requirements.';
    default:
      return err?.message || 'Something went wrong. Please try again.';
  }
}

const ForgotPassword: React.FC = () => {
  const [stage, setStage] = useState<Stage>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [deliveryHint, setDeliveryHint] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  // Local no-AWS mode has no Cognito, so there is nothing to reset. The Login
  // screen is never shown in local mode (the app auto-authenticates), but guard
  // the direct-navigation case so the page degrades gracefully instead of
  // throwing an Amplify "not configured" error.
  if (isLocalMode()) {
    return (
      <div className="login-container">
        <div className="login-card">
          <ThemeToggle className="theme-toggle--login" />
          <h1>ORchestrate · OR Allocation</h1>
          <h2>Reset <em>password</em></h2>
          <p className="info-message">
            Password reset is not available in local mode — this build has no
            Cognito user pool.
          </p>
          <div className="login-footer">
            <Link to="/login" className="login-link">Back to login</Link>
            <LegalLinks />
          </div>
        </div>
      </div>
    );
  }

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setSubmitting(true);
    try {
      const output = await resetPassword({ username: email });
      const { nextStep } = output;
      if (nextStep.resetPasswordStep === 'CONFIRM_RESET_PASSWORD_WITH_CODE') {
        const dest = nextStep.codeDeliveryDetails?.destination;
        setDeliveryHint(dest ? `We sent a code to ${dest}.` : null);
        setStage('confirm');
      } else {
        // 'DONE' — no confirmation required (unusual for email recovery).
        setStage('done');
      }
    } catch (err: any) {
      setError(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!code) {
      setError('Please enter the code from your email');
      return;
    }
    if (!newPassword || !confirmPassword) {
      setError('Please enter and confirm your new password');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    const policyError = getPasswordPolicyError(newPassword);
    if (policyError) {
      setError(policyError);
      return;
    }

    setSubmitting(true);
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code.trim(),
        newPassword,
      });
      setStage('done');
    } catch (err: any) {
      setError(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <ThemeToggle className="theme-toggle--login" />
        <h1>ORchestrate · OR Allocation</h1>
        <h2>Reset <em>password</em></h2>

        {error && <div className="error-message">{error}</div>}

        {stage === 'request' && (
          <form onSubmit={handleRequest}>
            <p className="info-message">
              Enter the email for your account and we'll send you a code to reset
              your password.
            </p>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                disabled={submitting}
                autoComplete="email"
                autoFocus
              />
            </div>

            <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
              {submitting ? 'Sending code...' : 'Send reset code'}
            </button>
          </form>
        )}

        {stage === 'confirm' && (
          <form onSubmit={handleConfirm}>
            <p className="info-message">
              {deliveryHint || 'Check your email for a verification code.'} Enter
              it below along with your new password.
            </p>

            <div className="form-group">
              <label htmlFor="code">Verification code</label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                className="form-control"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter the 6-digit code"
                disabled={submitting}
                autoComplete="one-time-code"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">New password</label>
              <input
                id="newPassword"
                type="password"
                className="form-control"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                disabled={submitting}
                autoComplete="new-password"
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm new password</label>
              <input
                id="confirmPassword"
                type="password"
                className="form-control"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                disabled={submitting}
                autoComplete="new-password"
              />
            </div>

            <div className="password-requirements">
              <p>Password must:</p>
              <ul>
                {PASSWORD_RULES.map((rule) => (
                  <li key={rule}>{rule}</li>
                ))}
              </ul>
            </div>

            <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
              {submitting ? 'Resetting...' : 'Reset password'}
            </button>

            <div style={{ marginTop: '14px', textAlign: 'center' }}>
              <button
                type="button"
                className="login-link"
                onClick={() => {
                  setStage('request');
                  setError(null);
                  setCode('');
                }}
                disabled={submitting}
              >
                Use a different email
              </button>
            </div>
          </form>
        )}

        {stage === 'done' && (
          <>
            <p className="info-message">
              Your password has been reset. You can now sign in with your new
              password.
            </p>
            <button
              type="button"
              className="btn btn-primary btn-block"
              onClick={() => navigate('/login')}
            >
              Back to login
            </button>
          </>
        )}

        {stage !== 'done' && (
          <div className="login-footer">
            <Link to="/login" className="login-link">Back to login</Link>
            <LegalLinks />
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
